import React, { useCallback, useMemo } from 'react';
import EditorBackgroundColorIcon from '@atlaskit/icon/glyph/editor/background-color';
import { SpotlightTarget } from '@atlaskit/onboarding';
import VisuallyHidden from '@atlaskit/visually-hidden';
import {
	ActionsWrapper,
	LeftActionsWrapper,
	RightActionsWrapper,
} from '@atlassian/jira-business-app-wrapper';
import {
	useConditionalFormattingSidebar,
	ConditionalFormattingSidebar,
} from '@atlassian/jira-business-conditional-formatting-sidebar';
import {
	Filters,
	SimpleSearchField,
	useHideDoneItems,
	BUSINESS_FIELD_TYPES,
	SOFTWARE_FIELD_TYPES,
} from '@atlassian/jira-business-filters';
import {
	JWMShareButton,
	integrationTypes,
	AddPeopleWithAvatar,
} from '@atlassian/jira-business-invite-people';
import { MoreMenu } from '@atlassian/jira-business-more-menu';
import {
	LIST_EMBED_NUDGE_STEP,
	useNudgeStep,
} from '@atlassian/jira-business-onboarding-list-embed';
import { ThemedButton } from '@atlassian/jira-business-theme-components';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	FilterDropdownListNudgeAsync,
	MoreDropdownListNudgeAsync,
} from '@atlassian/jira-software-onboarding-nudges--next/src/async';
import { useExpandCollapseAllItems } from '../../../controllers/expand-collapse-all-items';
import { useJWMListFeatures } from '../../../controllers/features-context';
import { useGroupBy } from '../../../controllers/group-by';
import { useHierarchyConfig } from '../../../controllers/hierarchy/hierarchy-config';
import GroupByDropdown from './group-by-dropdown';
import messages from './messages';
import { useFilterProps } from './utils';

const conditionalFormattingIcon = <EditorBackgroundColorIcon label="" size="medium" />;

export const HeaderActions = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		inviteButtonInteraction,
		isShareButtonEnabled,
		isPresenceEnabled,
		hasFilterCustomFields,
		isSoftwareFieldsEnabled,
	} = useJWMListFeatures();

	const [hideDoneItems, setHideDoneItems] = useHideDoneItems();

	const [{ nudgeStep, shouldOpenModal }, { setShouldOpenModal }] = useNudgeStep();
	const handleDialogClose = useCallback(() => {
		if (shouldOpenModal) {
			setShouldOpenModal(false);
		}
	}, [setShouldOpenModal, shouldOpenModal]);

	const [
		{ hasExpandedAllItems, hasCollapsedAllItems },
		{ toggleExpandAllItems, collapseAllItems },
	] = useExpandCollapseAllItems({
		view: 'list',
	});

	const [groupByFieldId] = useGroupBy();

	const isGrouping = groupByFieldId != null;

	const { showFlatList } = useHierarchyConfig();

	const { formatMessage } = useIntl();

	const { hiddenQuickFilters, allowedAliasFieldId } = useFilterProps();

	const showShareButtonSpotlight = nudgeStep === LIST_EMBED_NUDGE_STEP;

	const projectType =
		ff('platform.atlassian.griffin-navigation-redesign') && isSoftwareFieldsEnabled
			? 'software'
			: 'business';

	const hasFeedbackButton = projectType === 'software';

	const shareButton = (
		<JWMShareButton
			shareTitle={messages.shareTitle}
			integrationType={integrationTypes.LIST}
			shareContentType="list"
			isAutoOpenDialog={shouldOpenModal}
			onDialogClose={handleDialogClose}
		/>
	);

	const handleExpandCollapseAllItems = useCallback(
		(operation: 'collapse' | 'expand') => {
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'expandCollapseAll', {
				operation,
			});
			if (operation === 'collapse') {
				collapseAllItems();
			} else if (operation === 'expand') {
				toggleExpandAllItems();
			}
		},
		[collapseAllItems, createAnalyticsEvent, toggleExpandAllItems],
	);
	const wrappedShareButton = showShareButtonSpotlight ? (
		<SpotlightTarget name={LIST_EMBED_NUDGE_STEP}>
			<div>{shareButton}</div>
		</SpotlightTarget>
	) : (
		shareButton
	);

	const searchBarLabelId = 'header-actions-search-field-label';
	const searchBar = (
		<>
			<VisuallyHidden id={searchBarLabelId}>{formatMessage(messages.searchLabel)}</VisuallyHidden>
			<SimpleSearchField
				placeholder={messages.simpleSearch}
				view="list"
				describedbyId={searchBarLabelId}
			/>
		</>
	);

	const addPeopleWithPresence = (
		<AddPeopleWithAvatar
			view="list"
			isPresenceEnabledInContext={isPresenceEnabled}
			isOpenNewTabOnClick={inviteButtonInteraction === 'openNewTab'}
		/>
	);

	const shareActionButton = isShareButtonEnabled && wrappedShareButton;
	const allowedFieldTypes = useMemo(
		() =>
			isSoftwareFieldsEnabled
				? new Set([...BUSINESS_FIELD_TYPES, ...SOFTWARE_FIELD_TYPES])
				: undefined,
		[isSoftwareFieldsEnabled],
	);

	const filters = (
		<FilterDropdownListNudgeAsync>
			<Filters
				view="list"
				allowedFieldTypes={allowedFieldTypes}
				allowCustomFields={hasFilterCustomFields}
				allowedAliasFieldId={allowedAliasFieldId}
				hiddenQuickFilters={hiddenQuickFilters}
			/>
		</FilterDropdownListNudgeAsync>
	);

	const groupByDropdown = <GroupByDropdown />;

	let onClickConditionalFormattingButton;
	let isConditionalFormattingSidebarOpen;
	const shouldShowConditionalFormatting = expValEquals(
		'list_view_conditional_formatting',
		'cohort',
		'variation',
	);

	if (shouldShowConditionalFormatting) {
		({ onClickConditionalFormattingButton, isConditionalFormattingSidebarOpen } =
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useConditionalFormattingSidebar({
				SidebarComponent: ConditionalFormattingSidebar,
			}));
	}

	const formattingRulesBtn = shouldShowConditionalFormatting ? (
		<ThemedButton
			onClick={onClickConditionalFormattingButton}
			iconBefore={conditionalFormattingIcon}
			isSelected={isConditionalFormattingSidebarOpen}
			appearance="subtle"
		>
			{formatMessage(messages.conditionalFormatting)}
		</ThemedButton>
	) : null;

	const moreMenu = (
		<MoreDropdownListNudgeAsync>
			<MoreMenu
				hideDoneItems={Boolean(hideDoneItems)}
				onChangeHideDoneItems={setHideDoneItems}
				onExpandCollapseAllItems={handleExpandCollapseAllItems}
				isExpandCollapseButtonEnabled={!showFlatList || isGrouping}
				hasExpandedAllItems={hasExpandedAllItems}
				hasCollapsedAllItems={hasCollapsedAllItems}
				hasFeedbackButton={hasFeedbackButton}
				projectType={projectType}
			/>
		</MoreDropdownListNudgeAsync>
	);

	return (
		<ActionsWrapper>
			<LeftActionsWrapper>
				{searchBar}
				{addPeopleWithPresence}
			</LeftActionsWrapper>
			<RightActionsWrapper>
				{shareActionButton}
				{filters}
				{groupByDropdown}
				{formattingRulesBtn}
				{moreMenu}
			</RightActionsWrapper>
		</ActionsWrapper>
	);
};

export default HeaderActions;
